import { useCallback, useMemo, useState } from 'react'
import zionDebug from '@fs/zion-debug'
import { getNestedOrFloatingElementById, getPersonName, getPersonRelationships, useSlsStateContext } from '@fs/zion-sls'
import { getAtRiskRelationshipGroups, hasAtRiskRelationships } from './utils/atRiskRelationships'
import { ADD_EXISTING_PERSON_ENTRY_POINTS, ADD_EXISTING_PERSON_STEPS } from './utils/addExistingPersonConstants'

// To turn this on, in the browser console: localStorage.debug = 'fs:rip:addExistingPerson'
const debug = zionDebug('rip:addExistingPerson')

/**
 * Resolves person ids to the shape the at-risk calculation needs.
 *
 * @param {Array<string>} personIds - Selected person ids
 * @param {Array<Object>} images - SLS images from state
 * @returns {Array<{id: string, name: string}>}
 */
const toMovingPersons = (personIds, images) =>
  personIds.map((id) => {
    const person = getNestedOrFloatingElementById(images, id)
    return { id, name: person ? getPersonName({ person, justOneName: true }) : '' }
  })

/**
 * Groups the relationships a move would break, for the given selection.
 *
 * Kept as a plain function so it can be called with a selection that is not in state yet, which is
 * how the selection step decides what to ask next.
 *
 * @param {Array<string>} personIds - Selected person ids
 * @param {Array<Object>} images - SLS images from state
 * @param {Array<string>} pendingMemberIds - Ids already staged to join the same group
 * @returns {Array<import('./utils/atRiskRelationships').AtRiskRelationshipGroup>}
 */
const buildAtRiskGroups = (personIds, images, pendingMemberIds) => {
  const relationshipsByPersonId = personIds.reduce((byId, id) => {
    const person = getNestedOrFloatingElementById(images, id)
    byId[id] = person ? getPersonRelationships(person, images, false) || [] : []
    return byId
  }, {})

  return getAtRiskRelationshipGroups({
    movingPersons: toMovingPersons(personIds, images),
    relationshipsByPersonId,
    pendingMemberIds,
  })
}

/**
 * @typedef {Object} AddExistingPersonResult
 * @property {Array<string>} personIds - Everyone the patron chose to move
 * @property {Array<string>} primaryPersonIds - Who the patron ticked as primary, empty unless the
 *   primary person step ran.
 */

/**
 * @typedef {Object} UseAddExistingPersonFlowResult
 * @property {Array<string>} dialogSteps - The confirmation steps this run needs, in order. Empty when
 *   there is nothing to ask. Members of {@link ADD_EXISTING_PERSON_STEPS}
 * @property {Array<{id: string, name: string}>} movingPersons - The selected people, with display names
 * @property {Array<import('./utils/atRiskRelationships').AtRiskRelationshipGroup>} atRiskGroups
 *   Relationships the move would break, grouped by moving person
 * @property {Array<string>} primaryPersonIds - Who the patron has ticked as primary
 * @property {(personIds: Array<string>) => {staged: boolean, saving: (Promise<void>|undefined)}}
 *   selectPersons - Commit the selection. `staged` says whether a confirmation step was raised; when
 *   it is false the flow finished here and `saving` carries whatever `onComplete` returned
 * @property {(personId: string) => void} togglePrimaryPerson - Tick or untick a primary person
 * @property {() => (Promise<void>|undefined)} completeFlow - Finish from whichever step is last,
 *   handing back whatever `onComplete` returned so the caller can wait on the save
 * @property {() => void} reset - Return the flow to its initial state
 */

/**
 * Decides which confirmation steps the Add Existing Person flow needs, and holds their answers.
 * Which step is currently showing is the dialog's business, not this hook's.
 *
 * Both steps are conditional: Previous Relationships fires once per action, not once per person, and
 * only when somebody would lose a relationship to a person left behind. Primary Person applies only
 * to a new group with more than one mover, since a group of one has its primary by definition.
 *
 * @param {Object} params
 * @param {string} params.entryPoint - One of {@link ADD_EXISTING_PERSON_ENTRY_POINTS}
 * @param {Array<string>} [params.pendingMemberIds] - Ids already staged to join the group
 * @param {(result: AddExistingPersonResult) => (Promise<void>|void)} params.onComplete - Called once
 *   every applicable step is answered. A promise is passed back to the caller so it can hold the
 *   surfaces open until the save lands.
 * @returns {UseAddExistingPersonFlowResult}
 */
export const useAddExistingPersonFlow = ({ entryPoint, pendingMemberIds = [], onComplete = () => {} }) => {
  const { images } = useSlsStateContext()
  const [dialogSteps, setDialogSteps] = useState([])
  const [selectedPersonIds, setSelectedPersonIds] = useState([])
  const [primaryPersonIds, setPrimaryPersonIds] = useState([])

  const movingPersons = useMemo(() => toMovingPersons(selectedPersonIds, images), [selectedPersonIds, images])
  const atRiskGroups = useMemo(
    () => buildAtRiskGroups(selectedPersonIds, images, pendingMemberIds),
    [selectedPersonIds, images, pendingMemberIds]
  )

  const reset = useCallback(() => {
    setDialogSteps([])
    setSelectedPersonIds([])
    setPrimaryPersonIds([])
  }, [])

  const selectPersons = useCallback(
    (personIds) => {
      const atRisk = buildAtRiskGroups(personIds, images, pendingMemberIds)
      const steps = []
      if (hasAtRiskRelationships(atRisk)) steps.push(ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS)
      if (entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.NEW_GROUP && personIds.length > 1) {
        steps.push(ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON)
      }

      debug('selection confirmed', {
        personIds,
        entryPoint,
        atRiskGroups: atRisk,
        dialogSteps: steps,
      })

      if (steps.length === 0) {
        debug('flow complete', { personIds, primaryPersonIds: [] })
        // No dialog opened, so nothing else will reset the flow.
        reset()
        return {
          staged: false,
          saving: onComplete({ personIds, primaryPersonIds: [] }),
        }
      }

      setSelectedPersonIds(personIds)
      setPrimaryPersonIds([])
      setDialogSteps(steps)
      return { staged: true, saving: undefined }
    },
    [images, entryPoint, pendingMemberIds, onComplete, reset]
  )

  const togglePrimaryPerson = useCallback((personId) => {
    setPrimaryPersonIds((current) =>
      current.includes(personId) ? current.filter((id) => id !== personId) : [...current, personId]
    )
  }, [])

  /**
   * Finishes from whichever step is last. Closing the dialog is the one edge that resets the flow,
   * so this leaves state alone.
   *
   * @returns {Promise<void>|undefined} Whatever `onComplete` returned, so the caller can wait on it.
   */
  const completeFlow = useCallback(() => {
    // Only the primary person step collects ticks.
    const chosenPrimaryPersonIds = dialogSteps.includes(ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON)
      ? primaryPersonIds
      : []

    debug('flow complete', { personIds: selectedPersonIds, primaryPersonIds: chosenPrimaryPersonIds })
    return onComplete({ personIds: selectedPersonIds, primaryPersonIds: chosenPrimaryPersonIds })
  }, [dialogSteps, onComplete, primaryPersonIds, selectedPersonIds])

  return {
    dialogSteps,
    movingPersons,
    atRiskGroups,
    primaryPersonIds,
    selectPersons,
    togglePrimaryPerson,
    completeFlow,
    reset,
  }
}

export default useAddExistingPersonFlow
